import {
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  InputGroup,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { uniqBy, isEmpty, prop } from "ramda";
import { FieldError } from "react-hook-form";
import { CheckCircleIcon, CloseIcon, WarningIcon } from "@chakra-ui/icons";

import useClickOutside from "hooks/useClickOutside";
import useDebouncedCallback from "hooks/useDebouncedCallback";
import { TOutput } from "utils/geo/api/Autocomplete";
import CInput from "components/Chakra/Input";
import StreetWhisperApiEndpoint from "utils/geo/api/StreetWhisper";
import { ILocationSuggestion } from "utils/geo";
import { TranslationKeys } from "utils/localization";
import { useLocalization } from "hooks/useLocalization";
import useApiEndpoint from "hooks/useApiEndpoint";

const SearchInput = forwardRef<
  HTMLInputElement,
  {
    initialValue?: string;
    onChange: (value: string) => void;
    onFocus: (isFocused: boolean, value: string) => void;
    onBlur: () => void;
    onTab?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onEnter?: (value: ILocationSuggestion) => void;
    rightIcon?: ReactNode;
    value?: string;
    name: string;
    error?: boolean;
    firstItem: ILocationSuggestion;
    isNewInsertForm: boolean;
    placeholder?: string;
  }
>(
  (
    {
      onChange,
      onFocus,
      onBlur,
      onTab,
      onEnter,
      rightIcon,
      value,
      name,
      error,
      firstItem,
      isNewInsertForm,
      placeholder,
    },
    ref,
  ) => (
    <CInput
      value={value}
      label={
        !isNewInsertForm && (
          <Text
            variant="form-label"
            color={error ? "ud-error" : "ud-grey-600"}
            data-test="offer.address.street.streetText"
          >
            Ulice
            <Text as="span" pl="3px" color="ud-error">
              *
            </Text>
          </Text>
        )
      }
      id={name}
      data-test="offer.address.street.searchInput"
      autoCorrect="off"
      ref={ref}
      placeholder={placeholder}
      fontWeight="bold"
      _placeholder={{ fontWeight: "normal" }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onEnter && onEnter(firstItem);
        }
        if (e.key === "Tab") {
          onTab && onTab(e);
        }
      }}
      onFocus={(e) => {
        onFocus(true, e.target.value);
      }}
      onChange={({ currentTarget: { value: val } }) => {
        onChange && onChange(val);
      }}
      onBlur={() => onBlur()}
      rightElement={!isNewInsertForm && rightIcon}
      backgroundColor={isNewInsertForm ? "ud-white" : "inherit"}
      borderColor={error ? "ud-error" : "ud-grey-200"}
    />
  ),
);

const StreetInput: React.FC<{
  onLocationSelect?: (location: ILocationSuggestion) => void;
  onInputChange?: (value: string) => void;
  withButton?: boolean;
  defaultSearchLocation?: string;
  withBorders?: boolean;
  name: string;
  village?: ILocationSuggestion;
  value?: ILocationSuggestion;
  error?: FieldError;
  customMessage?: ReactNode;
  customError?: TranslationKeys;
  isNewInsertForm: boolean;
  onTab?: () => void;
  placeholder?: string;
}> = ({
  onLocationSelect,
  name,
  village,
  value,
  error,
  customError = "offer.input.street.required",
  customMessage,
  onInputChange,
  isNewInsertForm = false,
  placeholder = "Zadejte ulici",
  onTab,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const [items, setItems] = useState<TOutput>([]);
  const { translate } = useLocalization();
  const prevVillage = useRef(village);
  const [clearIcon, setClearIcon] = useState(false);

  const { callEndpoint: streetWhisperApi, cancelRequest } = useApiEndpoint(
    StreetWhisperApiEndpoint,
  );

  const [street, setStreet] = useState<string | undefined>(value?.label || "");

  const closeModal = (): void => {
    setOpen(false);
  };

  const handleFetch = async (term?: string): Promise<void> => {
    if (!village || !village.id) {
      return;
    }
    cancelRequest();
    const { data } = await streetWhisperApi({
      term: term || "",
      villageId: village.id,
      villageString: village.label,
    });
    if (data) {
      const toRemoveIndex = data.findIndex((x) => x.id === "");
      const uniqueStreet = uniqBy(prop("label"), data);
      setItems(uniqueStreet);
      if (toRemoveIndex >= 0) {
        data.splice(toRemoveIndex, 1);
      }
    }
  };

  const setClearIconWrapper = (isHover: boolean): void => {
    setClearIcon(isHover);
  };

  useEffect(() => {
    if (
      prevVillage.current?.id &&
      prevVillage.current?.label === village?.label
    ) {
      return;
    }

    prevVillage.current = village;
    handleFetch();
    setStreet("");
    setError(false);
    setLoading(false);
    setItems([]);
    closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [village]);

  const popperRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useClickOutside(
    popperRef,
    () => {
      closeModal();
    },
    [inputRef],
  );

  useEffect(() => {
    const streetMatch = items.find((i) => i.label === street);

    if (streetMatch) {
      onLocationSelect && onLocationSelect(streetMatch);
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, street]);

  const onSelect = (location: ILocationSuggestion): void => {
    if (!location.label && typeof location === "string") {
      const searchLocation = items.find((item) =>
        item.label.includes(location),
      );
      if (searchLocation) {
        setStreet(searchLocation.label);
        onLocationSelect && onLocationSelect(searchLocation);
      }
    } else {
      setStreet(location.label);
      onLocationSelect && onLocationSelect(location);
    }
    closeModal();
  };

  const fetchStreet = useDebouncedCallback((streetValue: string) => {
    streetValue.length >= 1 && handleFetch(streetValue);
  }, 150);

  useEffect(() => {
    if (street) {
      fetchStreet(street);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [street]);

  const isMenuOpen = isOpen && !hasError;

  const rightIcon = (): JSX.Element => {
    if (clearIcon) {
      return (
        <CloseIcon
          cursor="pointer"
          color="gray.300"
          onClick={() => {
            setClearIconWrapper(false);
            setStreet("");
          }}
          onMouseLeave={() => setClearIconWrapper(false)}
          boxSize="15px"
        />
      );
    }

    if (street !== "" && isLoading) {
      return <Spinner color="ud-error" boxSize="20px" />;
    }

    if (items.find((item) => item.label === street)?.id) {
      return (
        <CheckCircleIcon
          color="ud-success"
          boxSize="20px"
          onMouseEnter={() => setClearIconWrapper(true)}
        />
      );
    }

    return (
      <WarningIcon
        color="ud-error"
        boxSize="20px"
        onMouseEnter={() => setClearIconWrapper(true)}
      />
    );
  };

  const onSearchInputChange = (val: string) => {
    setStreet(val);
    setError(false);
    onInputChange && onInputChange(val);
    setTimeout(() => {
      handleFetch(val);
      setOpen(true);
    }, 500);

    if (!val) {
      setItems([]);
    }
  };

  return (
    <Popover
      isOpen={isMenuOpen}
      autoFocus={false}
      placement="bottom-start"
      isLazy
      matchWidth
    >
      <PopoverTrigger>
        <Box data-test="offer.address.street">
          <InputGroup display="block">
            <SearchInput
              error={hasError || Boolean(error)}
              name={name}
              value={street}
              ref={inputRef}
              onBlur={() => {}}
              // onBlur={() => setOpen(false)}
              firstItem={items[0]}
              isNewInsertForm={isNewInsertForm}
              onTab={(e) => {
                if (isMenuOpen && items[0].label) {
                  onSelect(items[0]);
                  e.preventDefault();
                  onTab && onTab();
                }
              }}
              onEnter={(location) => onSelect(location)}
              onChange={onSearchInputChange}
              onFocus={(isFocused, val) => {
                if (val === "") {
                  setItems([]);
                  setTimeout(() => {
                    handleFetch(val);
                    setOpen(isFocused);
                  }, 500);

                  return;
                }
                handleFetch(val);
                setOpen(isFocused);
              }}
              rightIcon={rightIcon()}
              placeholder={placeholder}
            />
          </InputGroup>
          {Boolean(error) && (
            <Text variant="form-error">
              {customError ? translate(customError) : error?.message}
            </Text>
          )}
          {customMessage && <Text variant="form-message">{customMessage}</Text>}
        </Box>
      </PopoverTrigger>

      <PopoverContent
        w="100%"
        mt="-5px"
        data-test="offer.address.street.popover"
      >
        <PopoverBody p="0" ref={popperRef} _focus={{ boxShadow: "0" }}>
          {!hasError && isEmpty(items) && (
            <Box p="10px">
              <Text variant="small_1">
                Začněte psát a my vám zkusíme poradit.
              </Text>
            </Box>
          )}

          <Box _last={{ div: { borderBottomRadius: "7px" } }}>
            {!isEmpty(items) &&
              (items as Array<TOutput[0]>).map((item, i) => {
                const suggestion = typeof item === "string" ? item : item.label;

                if (!item.id) {
                  return (
                    <Box key={i} px="15px" py="10px">
                      <Text variant="small_1" color="ud-grey-600">
                        {suggestion}
                      </Text>
                    </Box>
                  );
                }

                return (
                  <Stack
                    key={i}
                    px="15px"
                    py="10px"
                    direction="row"
                    align="center"
                    justify="space-between"
                    transition="all 333ms ease 0s"
                    _hover={{ background: "#F1F1F1", cursor: "pointer" }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSelect(item)}
                  >
                    <Stack direction="row" align="center">
                      <Text>{suggestion}</Text>
                    </Stack>
                  </Stack>
                );
              })}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default StreetInput;
